import React from "react";
import { Payor } from "@app/provider/payor";

import { getPayorOptionId } from "./payorsTypeAheadIds";

const MENU_CLASS = "payors-type-ahead__menu";

const findPayorIndex = (payor: Payor | undefined, options: Payor[]) => {
  if (!payor) {
    return -1;
  }

  return options.findIndex(
    (option) =>
      option.displayName.toLowerCase() === payor.displayName.toLowerCase()
  );
};

const getFocusedOptionId = (listbox: Element | null | undefined) => {
  if (!listbox) {
    return undefined;
  }

  for (const option of listbox.querySelectorAll<HTMLElement>(
    '[role="option"]'
  )) {
    if (
      option.classList.contains("mdc-list-item--focused") ||
      option.classList.contains("mdc-list-item--activated") ||
      option.getAttribute("tabindex") === "0"
    ) {
      return option.id;
    }
  }

  return undefined;
};

const findSelectedOptionIdInListbox = (
  listbox: Element | null | undefined,
  payor: Payor | undefined,
  options: Payor[]
) => {
  if (!listbox || !payor) {
    return undefined;
  }

  const byAriaSelected = listbox.querySelector<HTMLElement>(
    '[role="option"][aria-selected="true"]'
  );
  if (byAriaSelected?.id) {
    return byAriaSelected.id;
  }

  for (const option of listbox.querySelectorAll<HTMLElement>(
    '[role="option"]'
  )) {
    if (
      option.textContent?.trim().toLowerCase() ===
      payor.displayName.toLowerCase()
    ) {
      return option.id;
    }
  }

  const selectedIndex = findPayorIndex(payor, options);
  return selectedIndex >= 0 ? getPayorOptionId(selectedIndex) : undefined;
};

const isRelevantMutation = (record: MutationRecord) => {
  const target = record.target;

  if (!(target instanceof Element)) {
    return false;
  }

  return (
    target.closest(".payors-type-ahead-container") !== null ||
    target.closest(`.${MENU_CLASS}`) !== null
  );
};

export interface UsePayorsTypeAheadAriaOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  selectedPayor?: Payor;
  labelId?: string;
  listboxId: string;
  optionsRef: React.RefObject<Payor[]>;
}

export const usePayorsTypeAheadAria = ({
  containerRef,
  selectedPayor,
  labelId,
  listboxId,
  optionsRef,
}: UsePayorsTypeAheadAriaOptions) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [optionsVersion, setOptionsVersion] = React.useState(0);

  const notifyOptionsChanged = React.useCallback(() => {
    setOptionsVersion((version) => version + 1);
  }, []);

  const getInputElement = React.useCallback(
    () =>
      containerRef.current?.querySelector<HTMLInputElement>(
        "input.mdc-text-field__input"
      ),
    [containerRef]
  );

  const getPayorsList = React.useCallback(
    () =>
      document
        .querySelector(`.${MENU_CLASS}`)
        ?.querySelector(".typeahead-menu__items"),
    []
  );

  const isMenuOpen = React.useCallback(() => {
    const menuSurface = document.querySelector<HTMLElement>(`.${MENU_CLASS}`);
    return (
      !!menuSurface &&
      !menuSurface.classList.contains("mdc-menu-surface--closed")
    );
  }, []);

  const openMenu = React.useCallback((input: HTMLInputElement) => {
    input.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: " ",
        code: "Space",
        bubbles: true,
        cancelable: true,
      })
    );
  }, []);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resolveActiveOptionId = (payorsList: Element | null | undefined) => {
      if (selectedPayor) {
        const selectedId = findSelectedOptionIdInListbox(
          payorsList,
          selectedPayor,
          optionsRef.current
        );

        if (selectedId && document.getElementById(selectedId)) {
          return selectedId;
        }
      }

      if (isMenuOpen()) {
        return getFocusedOptionId(payorsList);
      }

      return undefined;
    };

    const applyAriaActivedescendant = (optionId: string | undefined) => {
      const input = getInputElement();
      if (!input) {
        return;
      }

      if (optionId && document.getElementById(optionId)) {
        input.setAttribute("aria-activedescendant", optionId);
      } else if (!selectedPayor) {
        input.removeAttribute("aria-activedescendant");
      }
    };

    const sync = () => {
      const payorsList = getPayorsList();

      if (payorsList) {
        payorsList.setAttribute("id", listboxId);
        payorsList.setAttribute("role", "listbox");
        if (labelId) {
          payorsList.setAttribute("aria-labelledby", labelId);
        }
      }

      setIsExpanded(isMenuOpen());
      applyAriaActivedescendant(resolveActiveOptionId(payorsList));
    };

    sync();

    const observer = new MutationObserver((records) => {
      if (records.some(isRelevantMutation)) {
        sync();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["tabindex", "class", "aria-selected"],
    });

    return () => observer.disconnect();
  }, [
    containerRef,
    getInputElement,
    getPayorsList,
    isMenuOpen,
    labelId,
    listboxId,
    optionsRef,
    optionsVersion,
    selectedPayor,
  ]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") {
        return;
      }

      const input = getInputElement();
      if (!input) {
        return;
      }

      const target = event.target as HTMLElement;
      if (target !== input && !target.closest(`.${MENU_CLASS}`)) {
        return;
      }

      event.preventDefault();

      if (!isMenuOpen()) {
        event.stopImmediatePropagation();
        openMenu(input);
      }
    };

    container.addEventListener("keydown", handleKeyDown, true);
    return () => container.removeEventListener("keydown", handleKeyDown, true);
  }, [containerRef, getInputElement, isMenuOpen, openMenu]);

  return { isExpanded, notifyOptionsChanged };
};
